import {
  Component,
  DestroyRef,
  forwardRef,
  OnInit,
  Type,
  inject,
  viewChild,
  Signal,
  ChangeDetectionStrategy,
  input,
  InputSignal,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  BehaviorSubject,
  firstValueFrom,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import { filter } from 'rxjs/operators';
import { QuestionStep } from '../../../models/step/question-step.model';
import { DynamicComponentLoader } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { QuestionInstance } from '../../../shared/enums/question-instance.enum';
import { QuestionTypeNotLoadedError } from '../errors/question-type-not-loaded.error';
import { GapMatchQuestionComponent } from './gap-match-question/gap-match-question.component';
import { KeywordQuestionComponent } from './keyword-question/keyword-question.component';
import { ListQuestionComponent } from './list-question/list-question.component';
import { MultipleChoiceQuestionComponent } from './multiple-choice-question/multiple-choice-question.component';
import { OpenQuestionComponent } from './open-question/open-question.component';
import { QuestionControlPlaceholderDirective } from './question-control-placeholder.directive';
import { QuestionAnswer, QuestionControlType } from './question-type.interface';
import { RadioQuestionComponent } from './radio-question/radio-question.component';
import { ReadingQuestionComponent } from './reading-question/reading-question.component';
import { SelfExamQuestionComponent } from './self-exam-question/self-exam-question.component';
import { SliderQuestionComponent } from './slider-question/slider-question.component';
import { StarQuestionComponent } from './star-question/star-question.component';
import { UnsupportedQuestionComponent } from './unsupported-question/unsupported-question.component';
import { Answer } from '../../../models/answer/answer.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlfredClientConfig } from '../../../alfred-client.config';

type UnsupportedType = 'UNSUPPORTED';
const UNSUPPORTED: UnsupportedType = 'UNSUPPORTED';
type OnChangeCallback = (newValue: QuestionAnswer) => void;
type OnTouchCallback = () => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Components = Type<QuestionControlType | any>;
type FocusElement = HTMLInputElement | HTMLTextAreaElement | null;
type GetElement = () => Element | null;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-question-control',
  templateUrl: './question-control.component.html',
  styleUrls: ['./question-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuestionControl),
      multi: true,
    },
  ],
  imports: [QuestionControlPlaceholderDirective],
})
export class QuestionControl
  implements OnInit, OnChanges, ControlValueAccessor
{
  private readonly dynamicComponentLoader: DynamicComponentLoader = inject(
    DynamicComponentLoader,
  );
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly config: AlfredClientConfig = inject(AlfredClientConfig);

  /**
   * Static array of supports question enums.
   */
  private readonly supportedQuestions: Map<string, Components> = new Map<
    string,
    Components
  >([
    [QuestionInstance.QUESTION_OPEN, OpenQuestionComponent],
    [
      QuestionInstance.QUESTION_MULTIPLE_CHOICE,
      MultipleChoiceQuestionComponent,
    ],
    [QuestionInstance.QUESTION_GAP_MATCH, GapMatchQuestionComponent],
    [QuestionInstance.QUESTION_SELF_EXAM, SelfExamQuestionComponent],
    [QuestionInstance.QUESTION_SLIDER, SliderQuestionComponent],
    [QuestionInstance.QUESTION_STAR, StarQuestionComponent],
    [QuestionInstance.QUESTION_RADIO, RadioQuestionComponent],
    [QuestionInstance.QUESTION_LIST, ListQuestionComponent],
    [QuestionInstance.QUESTION_KEYWORD, KeywordQuestionComponent],
    [QuestionInstance.QUESTION_READING, ReadingQuestionComponent],
    [UNSUPPORTED, UnsupportedQuestionComponent],
  ]);

  /**
   * The question placeholder will be used to place the correct question
   * specific component inside by dynamic component loading.
   */
  private readonly questionPlaceholder: Signal<QuestionControlPlaceholderDirective> =
    viewChild.required(QuestionControlPlaceholderDirective);

  /**
   * The question that is currently being displayed in the template.
   */
  public readonly questionData: InputSignal<QuestionStep | undefined> = input<
    QuestionStep | undefined
  >(undefined);

  /**
   * BehaviorSubject containing the currently loaded QuestionStep.
   */
  private readonly questionStep$: BehaviorSubject<QuestionStep> =
    new BehaviorSubject<QuestionStep>(undefined as never);

  /**
   * BehaviorSubject which holds the latest change.
   */
  private readonly valueChanges$: BehaviorSubject<QuestionAnswer> =
    new BehaviorSubject<QuestionAnswer>(undefined as never);

  /**
   * Subscription containing the value change subscriber which sets the _valueChanges$
   * This subscription variables is need because we need to unsubscribe each time the
   * questionData() changes.
   */
  private valueChangesSubscription!: Subscription;

  /**
   * The current questionType instance. Don't target it directly, internal use only.
   */
  public _currentComponent!: QuestionControlType;

  /**
   * Replay subject which emits then the instance is completely loaded and we can
   * start using the writeValue method.
   */
  private instanceReadySubject: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * @inheritDoc
   */
  public ngOnInit(): void {
    this.createQuestionComponentInitializationSubscriber();
    this.createOnChangeSubscriber();
  }

  public ngOnChanges(changes: SimpleChanges<QuestionControl>): void {
    if (changes['questionData']) {
      const question: QuestionStep | undefined =
        changes['questionData'].currentValue;
      if (!question || !question.question) {
        return;
      }
      this.destroyCurrentInstance();
      this.questionStep$.next(question);
    }
  }

  /**
   * @inheritDoc
   */
  private onChange: OnChangeCallback = () => {
    /* Callback */
  };

  /**
   * @inheritDoc
   */
  private onTouch: OnTouchCallback = () => {
    /* Callback */
  };

  /**
   * @inheritDoc
   */
  public registerOnChange(fn: OnChangeCallback): void {
    this.onChange = fn;
  }

  /**
   * @inheritDoc
   */
  public registerOnTouched(fn: OnTouchCallback): void {
    this.onTouch = fn;
  }

  /**
   * Prefill the textarea when patching the parent form.
   */
  public writeValue(value: QuestionAnswer): void {
    if (!value) {
      return;
    }

    if (!this._currentComponent) {
      throw new QuestionTypeNotLoadedError();
    }

    this._currentComponent.ready().then(() => {
      this._currentComponent.answer = value;
    });
  }

  /**
   * Returns a promise. The component is ready to use which the promise
   * resolves.
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.instanceReadySubject);
  }

  /**
   * subscribe to the valueChanges subject and trigger the onChange callback on emit.
   */
  public createOnChangeSubscriber(): void {
    this.valueChanges$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // specifically check for undefined since null is a valid value
        filter((value) => value !== undefined),
      )
      .subscribe((value) => {
        this.onChange(value);
      });
  }

  /**
   * create a subscription which listens to the questionData. When questionData
   * emits it destroys the current questionType and loaded the new questionType
   * base on the given questionData.
   */
  private createQuestionComponentInitializationSubscriber(): void {
    this.questionStep$
      .pipe(
        //filter((questionStep) => !!questionStep),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((questionStep) => {
        if (this.supportedQuestions.has(questionStep.question.type)) {
          this.replaceQuestionComponent(
            questionStep,
            this.supportedQuestions.get(
              questionStep.question.type,
            ) as Type<QuestionControlType>,
          );
        } else {
          this.replaceQuestionComponent(
            questionStep,
            UnsupportedQuestionComponent,
          );
        }
      });
  }

  /**
   * The is the internal mechanism which injects a new component instead of the
   * current, set the data and subscription to the valueChanges of that question
   * type.
   */
  // eslint-disable-next-line max-lines-per-function
  private async replaceQuestionComponent(
    step: QuestionStep,
    component: Type<QuestionControlType>,
  ): Promise<void> {
    this.destroyCurrentInstance();
    const instance: QuestionControlType =
      this.dynamicComponentLoader.injectReplacementComponent<QuestionControlType>(
        this.questionPlaceholder().viewContainerRef,
        component,
      );
    instance.question = step.question;

    if (step.answer) {
      instance.answer = step.answer.answer as QuestionAnswer;
    }

    this._currentComponent = instance;
    await instance.ready();
    this.instanceReadySubject.next();
    this.instanceReadySubject.complete();

    if (instance.answer) {
      this.onChange(instance.getValue() as QuestionAnswer);
    }

    let answer: QuestionAnswer | undefined = instance.answer;
    if (instance instanceof GapMatchQuestionComponent) {
      const empty: boolean = instance.answer.every((item) => item === '');
      answer = empty ? undefined : instance.answer;
    }

    if (
      !answer &&
      !step.feedback &&
      instance.getFocusElement &&
      this.config.autoFocus
    ) {
      setTimeout(() => {
        const input: FocusElement | null = (
          instance.getFocusElement as GetElement
        )() as FocusElement | null;
        input?.focus();
      }, 150);
    }

    this.valueChangesSubscription = instance
      .onValueChanges()
      .subscribe((value) => {
        this.valueChanges$.next(value as QuestionAnswer);
        if (this.questionStep$.getValue()?.answer?.answer) {
          (this.questionStep$.getValue().answer as Answer).answer = value;
        }
      });
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (this._currentComponent?.setDisabledState) {
      this._currentComponent.setDisabledState(isDisabled);
    }
  }

  /**
   * Method the remove and unsubscribe any references to the current questionType
   */
  private destroyCurrentInstance(): void {
    this.instanceReadySubject = new ReplaySubject<void>();

    if (this._currentComponent) {
      this._currentComponent = undefined as never;
    }
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }

    this.valueChanges$.next(undefined as never);
  }
}
